import { useState } from "react";
import { PlayCircle, Square, RotateCcw, SkipForward } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  GameStatus,
  useBingoGameStore,
} from "@/plugins/bingo/state/bingoGameStore";

/**
 * GameLifecycleControls: New Game · End Game · Reset (with safety confirmation).
 * "Reset" in Phase 1 semantics = end current game AND start a new fresh sequence.
 * During GAME_SUMMARY, exposes a "Continue Now" button to skip the 12s auto-return.
 */
export default function GameLifecycleControls() {
  const status = useBingoGameStore((s) => s.status);
  const newGame = useBingoGameStore((s) => s.newGame);
  const endGame = useBingoGameStore((s) => s.endGame);
  const dismissSummary = useBingoGameStore((s) => s.dismissSummary);
  const loading = useBingoGameStore((s) => s.loading);

  const [resetOpen, setResetOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);

  const inSession =
    status !== GameStatus.IDLE && status !== GameStatus.GAME_SUMMARY;
  const isSummary = status === GameStatus.GAME_SUMMARY;

  return (
    <div className="flex flex-col gap-2">
      {isSummary && (
        <button
          data-testid="host-continue-now"
          type="button"
          onClick={dismissSummary}
          className="h-12 rounded-md font-semibold text-black transition-all hover:brightness-110 active:scale-[0.99] inline-flex items-center justify-center gap-2"
          style={{
            background: "var(--ic-primary)",
            boxShadow: "0 0 20px var(--ic-primary-glow)",
          }}
        >
          <SkipForward className="w-4 h-4" strokeWidth={2.5} />
          Continue Now
        </button>
      )}

      <button
        data-testid="host-new-game"
        type="button"
        disabled={loading}
        onClick={() => (inSession ? setResetOpen(true) : newGame())}
        className="h-12 rounded-md font-semibold transition-all disabled:opacity-50 hover:brightness-110 active:scale-[0.99] inline-flex items-center justify-center gap-2"
        style={{
          background: isSummary
            ? "var(--ic-bg-elevated)"
            : "var(--ic-primary)",
          color: isSummary ? "white" : "black",
          border: isSummary
            ? "1px solid var(--ic-border-subtle)"
            : "none",
          boxShadow: isSummary ? "none" : "0 0 20px var(--ic-primary-glow)",
        }}
      >
        <PlayCircle className="w-4 h-4" strokeWidth={2.5} />
        {inSession ? "New Game (reset)" : "New Game"}
      </button>

      <button
        data-testid="host-end-game"
        type="button"
        disabled={!inSession || loading}
        onClick={() => setEndOpen(true)}
        className="h-10 rounded-md text-sm border border-[color:var(--ic-border-subtle)] text-white hover:border-[color:var(--ic-border-focus)] disabled:opacity-40 inline-flex items-center justify-center gap-2"
      >
        <Square className="w-4 h-4" />
        End Game
      </button>

      {/* Reset confirmation */}
      <AlertDialog open={resetOpen} onOpenChange={setResetOpen}>
        <AlertDialogContent data-testid="host-reset-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-4 h-4" />
              Start a new game?
            </AlertDialogTitle>
            <AlertDialogDescription>
              The current game will be closed and a fresh 1–90 sequence will be
              generated. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="host-reset-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="host-reset-confirm-btn"
              onClick={() => {
                setResetOpen(false);
                newGame();
              }}
            >
              Reset & Start New
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* End confirmation */}
      <AlertDialog open={endOpen} onOpenChange={setEndOpen}>
        <AlertDialogContent data-testid="host-end-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>End the game?</AlertDialogTitle>
            <AlertDialogDescription>
              The Projector will show the Game Summary. You can start a new game
              afterwards.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="host-end-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="host-end-confirm-btn"
              onClick={async () => {
                setEndOpen(false);
                await endGame();
              }}
            >
              End Game
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
